import * as Yup from 'yup';
import React, { useState } from 'react';
import { useSnackbar } from 'notistack';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link as RouterLink } from '@tanstack/react-router';
import { RHFSelect, FormProvider, RHFTextField } from '@emfood/shared';

import { LoadingButton } from '@mui/lab';
import {
  Stack,
  Dialog,
  Button,
  MenuItem,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { paths } from 'src/routes/paths';

import { RHFUpload } from 'src/components/hook-form/rhf-upload';
import { useCreateOrderIssue, useUploadOrderIssueImage } from 'src/api/hooks/api-hooks';
import { OrderIssueType, OrderIssueResolutionType } from 'src/api/models/orders';

// Props for the ReportOrderIssueDialog component
interface ReportOrderIssueDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  orderId: string;
  title?: string;
}

const MAX_PHOTO_SIZE_BYTES = 5 * 1024 * 1024;

const validatePhotoSize = (file: File) =>
  file.size > MAX_PHOTO_SIZE_BYTES
    ? { code: 'file-too-large', message: 'This photo is larger than 5MB' }
    : null;

const ReportOrderIssueSchema = Yup.object().shape({
  issueType: Yup.string().label('Report Order Issue').required(),
  details: Yup.string().label('More Information').required(),
  resolutionType: Yup.string().label('Resolution Type').required(),
  photos: Yup.array().of(Yup.mixed<File>().required()).max(5, 'You can attach up to 5 photos'),
});

const ReportOrderIssueDialog: React.FC<ReportOrderIssueDialogProps> = ({
  open,
  onClose,
  onSubmit,
  orderId,
  title = 'Please provide details of the issue with your order and select a preferred resolution',
}) => {
  const { mutateAsync: createOrderIssue } = useCreateOrderIssue();
  const { mutateAsync: uploadOrderIssueImage } = useUploadOrderIssueImage();

  const defaultValues = {
    issueType: '',
    details: '',
    resolutionType: '',
    photos: [] as File[],
  };

  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm({
    resolver: yupResolver(ReportOrderIssueSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
    reset,
  } = methods;

  const handleFormSubmit = handleSubmit(async (data) => {
    try {
      const imageUrls = await Promise.all(
        (data.photos ?? []).map(async (file) => {
          const fileFormData = new FormData();
          fileFormData.append('file-data', file);
          const uploadResult = await uploadOrderIssueImage({
            body: fileFormData,
            pathParams: { orderId },
          });
          return uploadResult.result;
        })
      );

      const submitData = {
        issueType: Number(data.issueType),
        details: data.details,
        resolutionType: Number(data.resolutionType),
        images: imageUrls,
      };
      await createOrderIssue({ body: submitData, pathParams: { id: orderId } });

      setSuccessConfirmationIsOpen(true);
      reset();
      onSubmit();
    } catch {
      enqueueSnackbar('Your issue was not submitted. Please try again.', { variant: 'error' });
    }
  });

  const [exitConfirmationIsOpen, setExitConfirmationIsOpen] = useState(false);
  const [successConfirmationIsOpen, setSuccessConfirmationIsOpen] = useState(false);

  const issueType = methods.watch('issueType');

  // const getResolutionOptions = () => {
  //   switch (issueType) {
  //     case 'missing':
  //     case 'temperature':
  //       return <MenuItem value="refund">Call back</MenuItem>;
  //     case 'note':
  //       return <MenuItem value="other">Reissue document</MenuItem>;
  //     default:
  //       return null;
  //   }
  // };

  return createPortal(
    <FormProvider methods={methods} onSubmit={handleFormSubmit}>
      <Dialog open={open && !successConfirmationIsOpen} onClose={onClose}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Stack sx={{ mt: 2 }} spacing={2}>
            <RHFSelect name="issueType" label="Issue Type" fullWidth>
              <MenuItem value={OrderIssueType['Delivery Date']}>
                Incorrect delivery date or time
              </MenuItem>
              <MenuItem value={OrderIssueType['Delivery Temperature']}>
                Temperature issue with whole delivery
              </MenuItem>
              <MenuItem value={OrderIssueType['Delivery Note']}>
                Delivery note or invoice error
              </MenuItem>
              <MenuItem value={OrderIssueType['Delivery Unstable']}>
                Unstable or unpalletised load
              </MenuItem>
              <MenuItem value={OrderIssueType['Delivery Vehicle']}>
                Driver or delivery vehicle safety
              </MenuItem>
              <MenuItem value={OrderIssueType['Delivery Feedback']}>Feedback</MenuItem>
            </RHFSelect>
            <RHFTextField
              name="details"
              label="Please add detail to explain your issue"
              fullWidth
              multiline
              placeholder="Write in your own words a summary of the issue"
              minRows={3}
            />
            <RHFSelect
              name="resolutionType"
              label="What resolution would you like to receive?"
              fullWidth
              disabled={!issueType}
            >
              <MenuItem value={OrderIssueResolutionType['Call Back']}>Call Back</MenuItem>
              <MenuItem value={OrderIssueResolutionType['Reissue Document']}>
                Reissue Document
              </MenuItem>
              <MenuItem value={OrderIssueResolutionType['No Action']}>No action</MenuItem>
            </RHFSelect>
            <Stack spacing={1}>
              <Typography variant="subtitle2">Add photos (optional)</Typography>
              <RHFUpload
                name="photos"
                multiple
                thumbnail
                accept={{ 'image/*': ['.jpeg', '.jpg', '.png'] }}
                validator={validatePhotoSize}
                helperText="Up to 5 photos, 5MB each. Photos of the delivery, labels or paperwork help the supplier resolve your issue faster."
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExitConfirmationIsOpen(true)}>Cancel</Button>
          <LoadingButton
            loading={isSubmitting}
            onClick={handleFormSubmit}
            variant="contained"
            disabled={isSubmitting || !isDirty}
          >
            Save Changes
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <Dialog open={exitConfirmationIsOpen} onClose={() => setExitConfirmationIsOpen(false)}>
        <DialogTitle>
          You have an unfinished issue log. Would you like to continue to submit your issue or
          discard and cancel your issue log?
        </DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              onClose();
              reset();
              setExitConfirmationIsOpen(false);
            }}
          >
            Discard
          </Button>
          <Button onClick={() => setExitConfirmationIsOpen(false)} variant="contained">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={successConfirmationIsOpen} onClose={() => setSuccessConfirmationIsOpen(false)}>
        <DialogTitle>Thank you.</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom>
            Your order issue has been submitted. We will contact you with a resolution shortly.
          </Typography>
          <Typography gutterBottom>
            A record has been sent to your email and is stored in the issue log.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Stack gap={2} sx={{ width: '100%' }}>
            <Button
              variant="outlined"
              LinkComponent={RouterLink}
              href={paths.dashboard.issues.root}
              onClick={() => {
                reset();
              }}
            >
              View Issue Log
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                onClose();
                reset();
                setSuccessConfirmationIsOpen(false);
              }}
            >
              Close
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </FormProvider>,
    document.body
  );
};

export default ReportOrderIssueDialog;
